import { ArrowRightIcon, FolderIcon } from "lucide-react";
import React from "react";

import { CHANGE_TYPE_CLASSES, CHANGE_TYPE_LABELS, DiffItemRow } from "./VersionDiffViewer";
import { isProviderReviewItem } from "./utils/review-items";
import { formatSkillRelativePathForDisplay } from "./utils/path-utils";
import type { SkillCollectionReviewItem } from "./store";

import LoadingSpinner from "@/components/LoadingSpinner";

interface ReviewChangesPanelProps {
  reviewItems: SkillCollectionReviewItem[];
  description: string;
  isReady: boolean;
  isLoadingNoticeVisible?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  originalContentByPath?: Record<string, string>;
  isLoadingOriginals?: boolean;
}

interface ReviewFileChangesContentProps {
  fileReviewItems: SkillCollectionReviewItem[];
  hasProviderReviewItems: boolean;
  emptyMessage: string;
  originalContentByPath: Record<string, string>;
  isLoadingOriginals: boolean;
}

const ReviewFileChangesContent = ({
  fileReviewItems,
  hasProviderReviewItems,
  emptyMessage,
  originalContentByPath,
  isLoadingOriginals,
}: ReviewFileChangesContentProps) => {
  if (fileReviewItems.length === 0) {
    if (hasProviderReviewItems) {
      return null;
    }

    return (
      <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return fileReviewItems.map(item =>
    item.isEmptyFolder ? (
      <div
        key={item.key}
        className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-4 py-3"
      >
        <div className="flex min-w-0 items-center gap-2">
          <FolderIcon className="size-4 shrink-0 text-gray-400" />
          <span className="truncate text-sm font-medium text-gray-900">
            {formatSkillRelativePathForDisplay(item.path)}
          </span>
          <span className="shrink-0 text-xs text-gray-400">Empty folder</span>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CHANGE_TYPE_CLASSES[item.changeType]}`}
        >
          {CHANGE_TYPE_LABELS[item.changeType]}
        </span>
      </div>
    ) : (
      <DiffItemRow
        key={item.key}
        item={item}
        originalContentByPath={originalContentByPath}
        isLoadingOriginals={isLoadingOriginals}
      />
    ),
  );
};

export const ReviewChangesPanel = ({
  reviewItems,
  description,
  isReady,
  isLoadingNoticeVisible = false,
  loadingMessage = "Preparing review...",
  emptyMessage = "No changes to review.",
  originalContentByPath = {},
  isLoadingOriginals = false,
}: ReviewChangesPanelProps) => {
  const providerReviewItems = reviewItems.filter(isProviderReviewItem);
  const fileReviewItems = reviewItems.filter(item => !isProviderReviewItem(item));

  return (
    <div className="min-h-0 rounded-md border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm font-medium text-gray-900">
          {reviewItems.length} change{reviewItems.length === 1 ? "" : "s"}
        </p>
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      </div>

      <div className="max-h-[55vh] space-y-2 overflow-y-auto p-4">
        {(isLoadingNoticeVisible || !isReady) && (
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
            <LoadingSpinner size={4} />
            {loadingMessage}
          </div>
        )}

        {isReady &&
          providerReviewItems.map(item => (
            <div
              key={item.key}
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
            >
              <span className="font-medium">Provider:</span>
              <span>{item.originalContent ?? "Universal"}</span>
              <ArrowRightIcon className="size-4 shrink-0 text-gray-400" />
              <span>{item.currentContent ?? "Universal"}</span>
            </div>
          ))}

        {isReady && (
          <ReviewFileChangesContent
            fileReviewItems={fileReviewItems}
            hasProviderReviewItems={providerReviewItems.length > 0}
            emptyMessage={emptyMessage}
            originalContentByPath={originalContentByPath}
            isLoadingOriginals={isLoadingOriginals}
          />
        )}
      </div>
    </div>
  );
};
